import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { FormPartComponent } from '@/app/shared/_components/form-part-component';
import { ApiService } from '@/app/shared/_services/api.service';
import { DspBuyer, DspBuyersQueryParams, TtdBuyerType } from '@/app/shared/_models/dsp-buyer.model';

@Component({
  selector: 'app-ttd-buyer-dropdown',
  templateUrl: './ttd-buyer-dropdown.component.html',
  providers: FormPartComponent.createDirectiveProviders(TtdBuyerDropdownComponent),
})
export class TtdBuyerDropdownComponent
  extends FormPartComponent<string, FormControl<string>>
  implements OnInit, OnDestroy {
  buyers: DspBuyer[] = [];
  isLoading = false;

  private _buyerType: TtdBuyerType = TtdBuyerType.Agency;
  private initialized = false;

  @Input()
  get buyerType(): TtdBuyerType {
    return this._buyerType;
  }
  set buyerType(value: TtdBuyerType) {
    if (this._buyerType === value) return;
    this._buyerType = value;
    if (this.initialized) {
      this.onBuyerTypeChange();
    }
  }

  private readonly _formPart = new FormControl<string>(null);
  private endCursor: string | null = null;
  private hasNextPage = false;
  private searchText = '';
  private searchSubject$ = new Subject<string>();
  private lastSearchText = '';
  private destroy$ = new Subject<void>();
  private cancelFetch$ = new Subject<void>();
  private selectedBuyerCache: DspBuyer | null = null;

  private readonly PAGE_SIZE = 50;
  private readonly SEARCH_DEBOUNCE_MS = 500;

  constructor(private apiService: ApiService) {
    super();
  }

  protected override get formPart(): FormControl<string> {
    return this._formPart;
  }

  override writeValue(value: string | string[]): void {
    const normalized = Array.isArray(value) ? value[0] || null : value;
    if (normalized) {
      this.resolveSelectedBuyer(normalized);
    }
    super.writeValue(normalized);
  }

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.fetchBuyers();
    this.initialized = true;
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  onSearch(searchText: string): void {
    this.searchSubject$.next(searchText);
  }

  onScrollToBottom(): void {
    if (this.hasNextPage && !this.isLoading) {
      this.fetchBuyers(true);
    }
  }

  private onBuyerTypeChange(): void {
    this._formPart.setValue(null);
    this.searchText = '';
    this.lastSearchText = '';
    this.fetchBuyers();
  }

  protected override getFormPartValidationErrors(): ValidationErrors {
    return null;
  }

  private setupSearchDebounce(): void {
    this.searchSubject$.pipe(
      debounceTime(this.SEARCH_DEBOUNCE_MS),
      filter(search => search !== this.lastSearchText),
      takeUntil(this.destroy$),
    ).subscribe(search => {
      this.lastSearchText = search;
      this.searchText = search;
      this.fetchBuyers();
    });
  }

  private resolveSelectedBuyer(buyerId: string): void {
    this.apiService.getDspBuyers('TTD', { buyerId, limit: 1 }).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res) => {
        const buyer = res.data.items[0];
        if (buyer) {
          this.selectedBuyerCache = buyer;
          this.prependCachedBuyer();
        }
      },
    });
  }

  private fetchBuyers(loadMore = false): void {
    this.cancelFetch$.next();

    if (!loadMore) {
      this.resetPagination();
    }

    this.isLoading = true;

    const params = this.buildQueryParams(loadMore);

    this.apiService.getDspBuyers('TTD', params).pipe(
      takeUntil(this.cancelFetch$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res) => {
        const newBuyers = res.data.items;
        this.buyers = loadMore ? [...this.buyers, ...newBuyers] : newBuyers;

        if (!loadMore) {
          this.prependCachedBuyer();
        }

        this.endCursor = res.data.meta.endCursor;
        this.hasNextPage = res.data.meta.hasNextPage;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private resetPagination(): void {
    this.buyers = [];
    this.endCursor = null;
    this.hasNextPage = false;
  }

  private buildQueryParams(loadMore: boolean): DspBuyersQueryParams {
    return {
      limit: this.PAGE_SIZE,
      buyerType: this.buyerType,
      ...(this.searchText ? { buyerId: this.searchText } : {}),
      ...(loadMore && this.endCursor ? { after: this.endCursor } : {}),
    };
  }

  private prependCachedBuyer(): void {
    if (this.selectedBuyerCache && !this.buyers.some(b => b.id === this.selectedBuyerCache.id)) {
      this.buyers = [this.selectedBuyerCache, ...this.buyers];
    }
  }
}
